import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarClock } from "lucide-react";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const bookingDate = (b) => (b.slot_start || b.scheduled_for || b.created_at || "").slice(0, 10);

// Month grid + selected-day detail over any list of service bookings.
export default function BookingsCalendar({ bookings = [], renderItem, onItemClick }) {
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [selected, setSelected] = useState(today.toISOString().slice(0, 10));

  const byDay = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      const d = bookingDate(b);
      if (!d) continue;
      (map[d] = map[d] || []).push(b);
    }
    return map;
  }, [bookings]);

  const first = new Date(cursor.y, cursor.m, 1);
  const startPad = first.getDay();
  const days = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(new Date(cursor.y, cursor.m, d).toISOString().slice(0, 10));

  const move = (delta) => {
    const nm = cursor.m + delta;
    setCursor({ y: cursor.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 });
  };
  const selectedList = byDay[selected] || [];

  return (
    <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]" data-testid="bookings-calendar">
      <div className="card-soft p-4">
        <div className="mb-3 flex items-center justify-between">
          <button onClick={() => move(-1)} data-testid="cal-prev" className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
          <p className="text-sm font-bold text-slate-800" data-testid="cal-month">{MONTHS[cursor.m]} {cursor.y}</p>
          <button onClick={() => move(1)} data-testid="cal-next" className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold text-slate-400">
          {DOW.map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="mt-1 grid grid-cols-7 gap-1">
          {cells.map((iso, i) => {
            if (!iso) return <div key={`p${i}`} />;
            const n = (byDay[iso] || []).length;
            const isSel = iso === selected;
            const isToday = iso === today.toISOString().slice(0, 10);
            return (
              <button key={iso} onClick={() => setSelected(iso)} data-testid={`cal-day-${iso}`}
                className={`relative aspect-square rounded-lg text-xs transition ${isSel ? "bg-slate-900 text-white" : n ? "bg-fuchsia-50 text-slate-700 hover:bg-fuchsia-100" : "text-slate-500 hover:bg-slate-50"} ${isToday && !isSel ? "ring-1 ring-fuchsia-400" : ""}`}>
                {Number(iso.slice(8, 10))}
                {n > 0 && <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 rounded-full px-1 text-[8px] font-bold ${isSel ? "bg-white/30 text-white" : "bg-fuchsia-600 text-white"}`}>{n}</span>}
              </button>
            );
          })}
        </div>
      </div>
      <div className="card-soft p-4" data-testid="cal-day-detail">
        <p className="mb-3 flex items-center gap-1.5 text-sm font-bold text-slate-800"><CalendarClock className="h-4 w-4 text-fuchsia-500" /> {new Date(selected).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</p>
        {selectedList.length === 0
          ? <p className="py-8 text-center text-xs text-slate-400" data-testid="cal-day-empty">No bookings on this day.</p>
          : <div className="space-y-2">{selectedList.map((b) => renderItem ? <div key={b.booking_id}>{renderItem(b)}</div> : (
              <button key={b.booking_id} type="button" onClick={() => onItemClick && onItemClick(b)} data-testid={`cal-item-${b.booking_id}`}
                className={`w-full rounded-lg border border-slate-100 p-2.5 text-left text-xs transition ${onItemClick ? "hover:border-fuchsia-300 hover:bg-fuchsia-50/40" : "cursor-default"}`}>
                <p className="font-semibold text-slate-800">{b.service_name || "Service"}</p>
                <p className="mt-0.5 text-slate-500">{b.slot_label || "Flexible"}{b.amount ? ` · ₹${b.amount}` : ""} · {(b.status || "").replace(/_/g, " ")}</p>
                {onItemClick && <span className="mt-1 inline-block text-[10px] font-semibold text-fuchsia-600">View details →</span>}
              </button>
            ))}</div>}
      </div>
    </div>
  );
}
